import { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  MessageSquare, Search, Settings, LogOut, Moon, Sun, 
  Plus, Users, User as UserIcon, Shield
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { 
  getChatName, getChatAvatar, isUserOnline, 
  formatTime, truncateText, getInitials 
} from '@/lib/utils';
import NewChatDialog from '@/components/NewChatDialog';

export const ChatSidebar = ({ 
  chats, 
  activeChat, 
  user, 
  onSelectChat, 
  onNewChat,
  onLogout,
  isDark,
  setIsDark,
  typingUsers,
  onOpenSettings,
  onOpenAdmin
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showNewChat, setShowNewChat] = useState(false);
  const [newChatType, setNewChatType] = useState('single');

  const filteredChats = chats.filter(chat => {
    const name = getChatName(chat, user?.id);
    return name.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const handleNewSingleChat = () => {
    setNewChatType('single');
    setShowNewChat(true);
  };

  const handleNewGroupChat = () => {
    setNewChatType('group');
    setShowNewChat(true);
  };

  return (
    <>
      <div className="h-full flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-slate-200 dark:border-slate-800">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
  {/* Dein Logo anstelle des MessageSquare-Icons */}
  <img 
    src="/logo.png" 
    alt="FreeBrain Logo" 
    className="w-10 h-10 rounded-xl object-cover" 
  />
  <h1 className="text-xl font-bold text-slate-900 dark:text-white">
    FreeBrain
  </h1>
</div>            <div className="flex items-center gap-2">
              {/* New Chat Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    className="rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800"
                    data-testid="new-chat-btn"
                  >
                    <Plus className="w-5 h-5 text-slate-600 dark:text-slate-400" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={handleNewSingleChat} data-testid="new-single-chat">
                    <UserIcon className="w-4 h-4 mr-2" />
                    Neuer Chat
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleNewGroupChat} data-testid="new-group-chat">
                    <Users className="w-4 h-4 mr-2" />
                    Neue Gruppe
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Settings Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    className="rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800"
                    data-testid="settings-btn"
                  >
                    <Settings className="w-5 h-5 text-slate-600 dark:text-slate-400" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={onOpenSettings}>
                    <Settings className="w-4 h-4 mr-2" />
                    Einstellungen
                  </DropdownMenuItem>
                  {user?.is_superadmin && (
                    <DropdownMenuItem onClick={onOpenAdmin}>
                      <Shield className="w-4 h-4 mr-2" />
                      Admin Dashboard
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setIsDark(!isDark)}>
                    {isDark ? (
                      <>
                        <Sun className="w-4 h-4 mr-2" />
                        Hell-Modus
                      </>
                    ) : (
                      <>
                        <Moon className="w-4 h-4 mr-2" />
                        Dunkel-Modus
                      </>
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={onLogout} className="text-red-500" data-testid="logout-btn">
                    <LogOut className="w-4 h-4 mr-2" />
                    Abmelden
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Chats durchsuchen..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 h-10 rounded-xl bg-slate-100 dark:bg-slate-800 border-transparent"
              data-testid="search-chats-input"
            />
          </div>
        </div>

        {/* Chat List */}
        <ScrollArea className="flex-1">
          <div className="p-2">
            {filteredChats.length === 0 ? (
              <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                {searchQuery ? 'Keine Chats gefunden' : 'Noch keine Chats'}
              </div>
            ) : (
              filteredChats.map((chat, index) => (
                <ChatItem
                  key={chat.id}
                  chat={chat}
                  user={user}
                  isActive={activeChat?.id === chat.id}
                  onClick={() => onSelectChat(chat)}
                  isTyping={!!typingUsers[chat.id]}
                  index={index}
                />
              ))
            )}
          </div>
        </ScrollArea>

        {/* User Profile */}
        <div className="p-4 border-t border-slate-200 dark:border-slate-800">
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10">
              <AvatarImage src={user?.avatar} />
              <AvatarFallback className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300">
                {getInitials(user?.username)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-slate-900 dark:text-white truncate">
                {user?.username}
              </p>
              <p className="text-sm text-slate-500 dark:text-slate-400 truncate">
                {user?.email}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* New Chat Dialog */}
      <NewChatDialog
        open={showNewChat}
        onOpenChange={setShowNewChat}
        type={newChatType}
        currentUser={user}
        onChatCreated={onNewChat}
      />
    </>
  );
};

const ChatItem = ({ chat, user, isActive, onClick, isTyping, index }) => {
  const name = getChatName(chat, user?.id);
  const avatar = getChatAvatar(chat, user?.id);
  const online = isUserOnline(chat, user?.id);
  const lastMessage = chat.last_message;

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.05 }}
      onClick={onClick}
      className={`
        flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-colors
        ${isActive 
          ? 'bg-emerald-50 dark:bg-emerald-900/20' 
          : 'hover:bg-slate-100 dark:hover:bg-slate-800'
        }
      `}
      data-testid={`chat-item-${chat.id}`}
    >
      <div className="relative">
        <Avatar className="h-12 w-12">
          <AvatarImage src={avatar} />
          <AvatarFallback className="bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
            {chat.is_group ? <Users className="w-5 h-5" /> : getInitials(name)}
          </AvatarFallback>
        </Avatar>
        {online && !chat.is_group && (
          <span className="absolute bottom-0 right-0 w-3 h-3 bg-emerald-500 border-2 border-white dark:border-slate-900 rounded-full" />
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <p className="font-semibold text-slate-900 dark:text-white truncate">
            {name}
          </p>
          {lastMessage && (
            <span className="text-xs text-slate-500 dark:text-slate-400">
              {formatTime(lastMessage.timestamp)}
            </span>
          )}
        </div>
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-500 dark:text-slate-400 truncate">
            {isTyping ? (
              <span className="text-emerald-500">schreibt...</span>
            ) : lastMessage ? (
              <>
                {lastMessage.sender_id === user?.id && 'Du: '}
                {lastMessage.type === 'image' ? '📷 Bild' : truncateText(lastMessage.content, 30)}
              </>
            ) : (
              'Noch keine Nachrichten'
            )}
          </p>
          {chat.unread_count > 0 && (
            <span className="unread-badge">{chat.unread_count}</span>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default ChatSidebar;
